import React, { useMemo, useState, useCallback } from "react";
import styled from "styled-components";
import { BRUTAL } from "../brutalist";
import {
  computeOperationalAlpha,
  persistOperatorProvisioning,
  buildDemoProtocolEntries,
} from "../../lib/operatorDiagnosticModel";
import { trackEvent } from "../../helpers/mixpanel";

const STEPS = [
  {
    key: "scale",
    title: "Portfolio scale",
    subtitle: "Number of asset instances under your control.",
  },
  {
    key: "friction",
    title: "Operational friction",
    subtitle: "Average hours per month spent on maintenance and turnovers.",
  },
  {
    key: "ceiling",
    title: "Trust threshold",
    subtitle: "Comfortable auto-commit ceiling (USD) per dispatch lane.",
  },
  {
    key: "review",
    title: "Efficiency projection",
    subtitle: "Gain-share lane model - read-only estimate.",
  },
];

/**
 * Multi-step portfolio diagnostic (brutalist). Sliders use discrete mechanical
 * updates (no ease curves on the shell).
 *
 * @param {{ onComplete?: (payload: { operatorId: string; diagnostic: object }) => void }} props
 */
export default function SystemDiagnostic({ onComplete }) {
  const [step, setStep] = useState(0);
  const [units, setUnits] = useState(3);
  const [frictionHours, setFrictionHours] = useState(24);
  const [ceiling, setCeiling] = useState(150);

  const model = useMemo(
    () =>
      computeOperationalAlpha({
        units,
        frictionHoursPerMonth: frictionHours,
        autoCommitCeiling: ceiling,
      }),
    [units, frictionHours, ceiling]
  );

  const projected = model.projectedAnnualOperationalAlpha;

  const goNext = useCallback(() => {
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }, []);

  const goBack = useCallback(() => {
    setStep((s) => Math.max(0, s - 1));
  }, []);

  const initiateProvisioning = useCallback(() => {
    const operatorId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `op_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    const diagnostic = {
      units,
      frictionHoursPerMonth: frictionHours,
      autoCommitCeiling: ceiling,
      projectedAnnualOperationalAlpha: projected,
      turnoverRate: model.turnoverRate,
      avgSavings: model.avgSavings,
    };
    const demoEntries = buildDemoProtocolEntries(
      { ...diagnostic, projectedAnnualOperationalAlpha: projected },
      operatorId
    );
    persistOperatorProvisioning({ operatorId, diagnostic, demoEntries });
    trackEvent("Portfolio Diagnostic Provisioned", {
      units,
      friction_hours: frictionHours,
      ceiling,
      projected_alpha: projected,
    });
    onComplete?.({ operatorId, diagnostic });
  }, [
    units,
    frictionHours,
    ceiling,
    projected,
    model.turnoverRate,
    model.avgSavings,
    onComplete,
  ]);

  const meta = STEPS[step];

  return (
    <Shell className="brutal-blueprint-bg">
      <Rail>
        <RailTitle>Engine · Portfolio diagnostic</RailTitle>
        <StepTicks aria-hidden>
          {STEPS.map((s, i) => (
            <Tick key={s.key} $on={i <= step} />
          ))}
        </StepTicks>
      </Rail>
      <Body>
        <Kicker>Step {step + 1} / {STEPS.length}</Kicker>
        <H2>{meta.title}</H2>
        <Sub>{meta.subtitle}</Sub>

        {meta.key === "scale" && (
          <Block>
            <Row>
              <MonoLabel>ASSET_INSTANCES</MonoLabel>
              <MonoVal>{units}</MonoVal>
            </Row>
            <MechRange
              type="range"
              min={1}
              max={250}
              step={1}
              value={units}
              onChange={(e) => setUnits(Number(e.target.value))}
              aria-valuetext={`${units} units`}
            />
            <Hint>1–250 doors / asset instances.</Hint>
          </Block>
        )}

        {meta.key === "friction" && (
          <Block>
            <Row>
              <MonoLabel>HOURS_PER_MONTH</MonoLabel>
              <MonoVal>{frictionHours}</MonoVal>
            </Row>
            <MechRange
              type="range"
              min={0}
              max={160}
              step={1}
              value={frictionHours}
              onChange={(e) => setFrictionHours(Number(e.target.value))}
            />
            <Hint>0–160 h/mo coordination load.</Hint>
          </Block>
        )}

        {meta.key === "ceiling" && (
          <Block>
            <Row>
              <MonoLabel>AUTO_COMMIT_USD</MonoLabel>
              <MonoVal>${ceiling}</MonoVal>
            </Row>
            <MechRange
              type="range"
              min={75}
              max={5000}
              step={25}
              value={ceiling}
              onChange={(e) => setCeiling(Number(e.target.value))}
            />
            <Hint>$75–$5000 · 25 USD steps.</Hint>
          </Block>
        )}

        {meta.key === "review" && (
          <Block>
            <AlphaPanel>
              <AlphaLabel>Illustrative model output</AlphaLabel>
              <AlphaCounter aria-live="polite" key={projected}>
                ${projected.toLocaleString()}
              </AlphaCounter>
              <AlphaMeta>
                Internal calculator only - not a promise of savings or
                performance. Uses your inputs and a fixed $49 per-event fee in
                the formula.
              </AlphaMeta>
            </AlphaPanel>
          </Block>
        )}

        <NavRow>
          {step > 0 ? (
            <MechButton type="button" onClick={goBack} $variant="ghost">
              ← BACK
            </MechButton>
          ) : (
            <span />
          )}
          {step < STEPS.length - 1 ? (
            <MechButton type="button" onClick={goNext} $variant="primary">
              NEXT →
            </MechButton>
          ) : (
            <MechButton
              type="button"
              onClick={initiateProvisioning}
              $variant="commit"
            >
              [INITIATE OPERATOR PROVISIONING]
            </MechButton>
          )}
        </NavRow>
      </Body>
    </Shell>
  );
}

const Shell = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 0;
  border: 2px solid ${BRUTAL.ink};
  background: ${BRUTAL.paperOff};
  color: ${BRUTAL.ink};
  font-family: ${BRUTAL.sansStack};
  min-height: 420px;
  transition: none;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Rail = styled.div`
  border-right: 2px solid ${BRUTAL.ink};
  padding: 1rem 0.75rem;
  background: ${BRUTAL.paper};
  transition: none;

  @media (max-width: 640px) {
    border-right: none;
    border-bottom: 2px solid ${BRUTAL.ink};
  }
`;

const RailTitle = styled.div`
  font-family: ${BRUTAL.monoStack};
  font-size: 9px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-weight: 800;
  color: ${BRUTAL.mute};
  line-height: 1.4;
`;

const StepTicks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
`;

const Tick = styled.div`
  height: 8px;
  border: 2px solid ${BRUTAL.ink};
  background: ${(p) => (p.$on ? BRUTAL.success : BRUTAL.paperOff)};
  transition: none;
`;

const Body = styled.div`
  padding: 1.25rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Kicker = styled.div`
  font-family: ${BRUTAL.monoStack};
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${BRUTAL.emergency};
  font-weight: 800;
`;

const H2 = styled.h2`
  margin: 0;
  font-size: 1.35rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1.15;
`;

const Sub = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.9rem;
  color: ${BRUTAL.mute};
  line-height: 1.5;
  max-width: 40rem;
`;

const Block = styled.div`
  margin-top: 0.5rem;
  padding: 1rem;
  border: 2px solid ${BRUTAL.ink};
  background: ${BRUTAL.paper};
  transition: none;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.65rem;
`;

const MonoLabel = styled.span`
  font-family: ${BRUTAL.monoStack};
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 700;
`;

const MonoVal = styled.span`
  font-family: ${BRUTAL.monoStack};
  font-size: 1.1rem;
  font-weight: 800;
`;

const MechRange = styled.input`
  width: 100%;
  height: 14px;
  accent-color: ${BRUTAL.ink};
  cursor: pointer;
  transition: none;

  &::-webkit-slider-thumb {
    transition: none;
  }
`;

const Hint = styled.p`
  margin: 0.5rem 0 0;
  font-family: ${BRUTAL.monoStack};
  font-size: 10px;
  color: ${BRUTAL.subtle};
  letter-spacing: 0.06em;
`;

const AlphaPanel = styled.div`
  border: 2px solid ${BRUTAL.ink};
  padding: 1rem;
  background: ${BRUTAL.ink};
  color: ${BRUTAL.paper};
  box-shadow: ${BRUTAL.shadowSm};
  transition: none;
`;

const AlphaLabel = styled.div`
  font-family: ${BRUTAL.monoStack};
  font-size: 10px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: ${BRUTAL.subtle};
  margin-bottom: 0.35rem;
`;

const AlphaCounter = styled.div`
  font-family: ${BRUTAL.monoStack};
  font-size: clamp(1.75rem, 5vw, 2.75rem);
  font-weight: 900;
  letter-spacing: 0.02em;
  color: ${BRUTAL.success};
  line-height: 1;
  transition: none;
`;

const AlphaMeta = styled.div`
  margin-top: 0.75rem;
  font-family: ${BRUTAL.monoStack};
  font-size: 10px;
  line-height: 1.45;
  color: ${BRUTAL.subtle};
`;

const NavRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 1.25rem;
  gap: 12px;
  flex-wrap: wrap;
`;

const MechButton = styled.button`
  font-family: ${BRUTAL.monoStack};
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.65rem 1rem;
  border: 2px solid ${BRUTAL.ink};
  cursor: pointer;
  transition: none;
  border-radius: 0;

  ${(p) =>
    p.$variant === "primary"
      ? `
    background: ${BRUTAL.ink};
    color: ${BRUTAL.paper};
    box-shadow: ${BRUTAL.shadowSm};
  `
      : p.$variant === "commit"
      ? `
    background: ${BRUTAL.success};
    color: ${BRUTAL.ink};
    box-shadow: ${BRUTAL.shadowSm};
  `
      : `
    background: ${BRUTAL.paper};
    color: ${BRUTAL.ink};
  `}

  &:active {
    transform: translate(2px, 2px);
    box-shadow: none;
  }
`;
